import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { useUser } from "../../UserContext";
import { Event } from "../Events";

interface Item {
  id: number;
  item_name: string;
  status: "Assigned" | "Unassigned";
  assignee: string | null;
  assignee_avatar_url: string | null;
}

interface ChecklistProps {
  eventData: Event;
}

const Checklist: React.FC<ChecklistProps> = ({ eventData }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [open, setOpen] = useState(false);
  const [newItem, setNewItem] = useState("");
  const { currentUser, selectedAvatar } = useUser();

  useEffect(() => {
    const fetchItems = async () => {
      if (!eventData.event_id) return;

      // Initialize items for the event
      await axios.post(
        `${import.meta.env.VITE_API_URL}/checklist/initialize-event-items/${
          eventData.event_id
        }`
      );

      // Fetch items for the event
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/checklist/event-items/${
          eventData.event_id
        }`
      );
      setItems(
        response.data.map((item: any) => ({
          id: item.id,
          item_name: item.item,
          status: item.status,
          assignee:
            item.status === "Assigned"
              ? `${item.first_name ?? ""} ${item.last_name ?? ""}`
              : null,
          assignee_avatar_url: item.assignee_avatar_url,
        }))
      );
    };

    fetchItems();
  }, [eventData.event_id]);

  const handleAssign = async (index: number) => {
    const newItems = [...items];
    const currentItem = newItems[index];
    const newStatus =
      currentItem.status === "Unassigned" ? "Assigned" : "Unassigned";

    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/checklist/event-items/${
          currentItem.id
        }`,
        {
          status: newStatus,
          assignee_id: newStatus === "Assigned" ? currentUser?.user_id : null,
          assignee_avatar_url: newStatus === "Assigned" ? selectedAvatar : null,
        }
      );

      currentItem.status = newStatus;
      currentItem.assignee =
        newStatus === "Assigned"
          ? currentUser
            ? `${currentUser.first_name} ${currentUser.last_name}`
            : null
          : null;
      currentItem.assignee_avatar_url =
        newStatus === "Assigned" ? selectedAvatar : null;
      setItems(newItems);
    } catch (error) {
      console.error("Error updating item status:", error);
    }
  };

  const handleAddItem = async () => {
    if (newItem.trim()) {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/checklist/event-items`,
        {
          event_id: eventData.event_id,
          item_name: newItem,
          assignee_avatar_url: null,
        }
      );
      setItems([
        ...items,
        {
          id: response.data.id,
          item_name: newItem,
          status: "Unassigned",
          assignee: null,
          assignee_avatar_url: null,
        },
      ]);
      setNewItem("");
      setOpen(false);
    }
  };

  return (
    <Box sx={{ mx: 5, mt: 2.5 }}>
      <Typography variant="h6" gutterBottom>
        Items to Bring
      </Typography>
      <List>
        {items.map((item, index) => (
          <ListItem key={item.id}>
            <ListItemText
              primary={item.item_name}
              secondary={
                item.status === "Unassigned"
                  ? "Unassigned"
                  : `Assigned to ${item.assignee}`
              }
            />
            {item.status === "Unassigned" ? (
              <Button
                variant="outlined"
                onClick={() => handleAssign(index)}
                sx={{ ml: 2 }}
              >
                Volunteer
              </Button>
            ) : (
              <>
                <Avatar
                  src={item.assignee_avatar_url ?? ""}
                  alt={item.assignee ? String(item.assignee) : ""}
                  onClick={() => handleAssign(index)}
                  sx={{ cursor: "pointer", ml: 2 }}
                />
              </>
            )}
          </ListItem>
        ))}
        <ListItem onClick={() => setOpen(true)}>
          <ListItemText primary="Add a new item" />
          <AddIcon />
        </ListItem>
      </List>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add New Item</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Item Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddItem} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Checklist;
